import { Button } from "@/components/ui/button";
import { SIGNAL_LABELS } from "@/lib/knowledge/documentHealth";
import type { DocumentHealthReport } from "@/types/knowledge/knowledge-brain";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ReviewQueueCardProps {
  health: DocumentHealthReport;
  isLoading: boolean;
  onStartReview: () => void;
}

/**
 * The entry point to the review queue: how many documents need attention and
 * why, with the not-yet-available signal shown honestly rather than hidden.
 */
const ReviewQueueCard = ({ health, isLoading, onStartReview }: ReviewQueueCardProps) => {
  const queueCount = health.queue.length;

  return (
    <section
      className="rounded-2xl border border-gray-200 bg-white"
      aria-labelledby="review-queue-heading"
    >
      <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
        <GenieIcon
          name="clipboard-check"
          size={24}
          color="#4E9CFF"
          secondaryColor="#FF4DB8"
          gradientEnabled
          label="Review queue"
          className="mt-0.5 flex-shrink-0"
        />
        <div className="min-w-0">
          <h2 id="review-queue-heading" className="text-base font-semibold text-gray-900">
            Review queue
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Documents worth a second look, one at a time.
          </p>
        </div>
      </div>

      <div className="space-y-4 px-5 py-5">
        {isLoading ? (
          <p className="text-sm text-gray-500">Checking your documents…</p>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              {queueCount === 0 ? (
                "Nothing needs attention right now."
              ) : (
                <>
                  <span className="text-2xl font-bold tabular-nums text-genie-primary">
                    {queueCount}
                  </span>{" "}
                  {queueCount === 1 ? "document needs" : "documents need"} a decision.
                </>
              )}
            </p>

            <dl className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-sm text-gray-600">{SIGNAL_LABELS.stale}</dt>
                <dd className="text-sm font-semibold tabular-nums text-gray-900">
                  {health.counts.stale}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-sm text-gray-600">{SIGNAL_LABELS.unused}</dt>
                <dd className="text-sm font-semibold tabular-nums text-gray-900">
                  {health.counts.unused}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-sm text-gray-400">{SIGNAL_LABELS.conflicting}</dt>
                <dd className="text-right text-xs font-medium text-gray-400">Not available yet</dd>
              </div>
            </dl>

            <p className="text-xs leading-relaxed text-gray-500">
              Spotting documents that contradict each other needs a record of which sources each
              answer came from. That is on the roadmap.
            </p>

            <Button
              onClick={onStartReview}
              disabled={queueCount === 0}
              className="w-full bg-gradient-to-r from-genie-primary to-genie-secondary font-semibold text-white shadow-lg shadow-genie-primary/30 transition-all hover:from-genie-primary-hover hover:to-genie-secondary hover:shadow-xl hover:shadow-genie-primary/40 disabled:opacity-50"
            >
              {queueCount === 0 ? "All clear" : "Start review"}
            </Button>
          </>
        )}
      </div>
    </section>
  );
};

export default ReviewQueueCard;
